import { useCallback, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import styled from "@emotion/styled/macro";
import { useCapContext } from "cap/providers/CapContext";
import { InHomeDesign } from "components/molecules/InHomeDesignMolecule";
import { useAppSelector } from "store";
import { IhdMessage } from "utils/IhdMessage";

type ChangeOrderDesignRenderProps = {
  offerId: string;
  setDesignId: (id: string) => void;
  setError: (error: Error | null) => void;
};

const ChangeOrderDesignRender = ({
  offerId,
  setDesignId,
  setError,
}: ChangeOrderDesignRenderProps) => {
  const { prospectId } = useParams();
  const { activeProposal } = useCapContext();

  const cognitoToken = useAppSelector(
    (state) => state.auth.cognitoToken?.token
  );

  const iFrameRef = useRef<HTMLIFrameElement>(null);

  const publishToIhd = useCallback((message: IhdMessage) => {
    iFrameRef.current?.contentWindow?.postMessage(message, "*");
  }, []);

  useEffect(() => {
    const handler = (event: MessageEvent) => {
      const message = event?.data as IhdMessage;
      if (!message) {
        return;
      }

      if (message.type === "QUERY:AUTH_TOKEN") {
        if (message.payload.type !== "IHD") return;

        const authMessage = {
          source: "Offer Experience",
          type: "EVENT:AUTH_TOKEN",
          payload: {
            type: "IHD",
            token: cognitoToken,
          },
        } as IhdMessage;

        publishToIhd(authMessage);
      } else if (message.type === "DESIGN_EDIT_COMPLETE") {
        setDesignId(message.payload?.designId || "");
      } else if (message.type === "UNRECOVERABLE_ERROR") {
        const cause = message.payload?.error || "Unknown error";
        setError(new Error("IHD Error", { cause }));
      }
    };
    window.addEventListener("message", handler);
    return () => {
      window.removeEventListener("message", handler);
    };
  }, [cognitoToken, publishToIhd, setDesignId, setError]);

  return (
    <DisplayNoneDiv>
      {prospectId && offerId && activeProposal && (
        <InHomeDesign
          ref={iFrameRef}
          offerId={offerId}
          prospectId={prospectId}
          skipToPricing
          parentProposalId={activeProposal.proposalId}
        />
      )}
    </DisplayNoneDiv>
  );
};

const DisplayNoneDiv = styled.div`
  display: none;
`;

export { ChangeOrderDesignRender };
